import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { MonthInput } from '../../../components/ui/month-input'
import { config } from '../../../lib/config'
import { supabase } from '../../../lib/supabase'
import { ReportByRowTemplate } from '../export-bill/components/ReportByRowTemplate'
import type { BillData } from '../export-bill/interfaces/exportBill'
import {
  fetchAllBillsByMonth,
  fetchLatestSnapshotMeta,
  fetchSnapshotBillsByMonth,
} from '../export-bill/services/exportBillService'

const EXPORT_BILL_URL = `${config.supabase.url}/functions/v1/export-bill`

function createDownloadBlob(blob: Blob): Blob {
  const forceDownloadTypes = ['application/pdf', 'application/zip']
  if (!blob.type || forceDownloadTypes.includes(blob.type)) {
    return new Blob([blob], { type: 'application/octet-stream' })
  }
  return blob
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const downloadBlob = createDownloadBlob(blob)
  const url = URL.createObjectURL(downloadBlob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  a.setAttribute('data-downloadurl', `${downloadBlob.type}:${filename}:${url}`)
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 100)
}

async function fetchExportBlob(
  exportType: 'report-pdf',
  yearMonth: string,
  roomNumbers: string[] | null,
): Promise<Blob> {
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const token = session?.access_token ?? ''
  const res = await fetch(EXPORT_BILL_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ exportType, yearMonth, roomNumbers }),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }))
    throw new Error(err.error ?? 'Export failed.')
  }
  return res.blob()
}

export default function ReportPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const today = new Date()
  const defaultYearMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`

  const queryMonth = searchParams.get('month')
  const [yearMonth, setYearMonth] = useState(queryMonth || defaultYearMonth)
  const [bills, setBills] = useState<BillData[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)
  const [dataSource, setDataSource] = useState<'snapshot' | 'live' | null>(null)
  const [snapshottedAt, setSnapshottedAt] = useState<string | null>(null)

  // Sync state to URL
  const handleMonthChange = (val: string) => {
    setYearMonth(val)
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (val) next.set('month', val)
      else next.delete('month')
      return next
    })
  }

  // Sync URL to state (for back/forward navigation)
  useEffect(() => {
    if (queryMonth && queryMonth !== yearMonth) {
      setYearMonth(queryMonth)
    }
  }, [queryMonth, yearMonth])

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setError(null)
      setBills([])
      setDataSource(null)
      setSnapshottedAt(null)
      setIsLoading(true)
      try {
        const snapshotBills = await fetchSnapshotBillsByMonth(yearMonth)
        if (cancelled) return
        if (snapshotBills.length > 0) {
          setBills(snapshotBills)
          setDataSource('snapshot')
          const meta = await fetchLatestSnapshotMeta(yearMonth)
          if (!cancelled) setSnapshottedAt(meta?.snapshottedAt ?? null)
        } else {
          const all = await fetchAllBillsByMonth(yearMonth)
          if (cancelled) return
          if (all.length === 0) {
            setError(`No records found for ${yearMonth}.`)
          } else {
            setBills(all)
            setDataSource('live')
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to fetch bills.')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [yearMonth])

  const exportReportPdf = async () => {
    if (bills.length === 0) return
    setIsExporting(true)
    try {
      const blob = await fetchExportBlob('report-pdf', yearMonth, null)
      triggerBlobDownload(blob, `report-${yearMonth}.pdf`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Report export failed.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-bold text-2xl text-gray-800">Report</h2>
      </div>

      {/* Filter */}
      <Card className="mb-6 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="yearMonth"
              className="font-medium text-gray-700 text-sm"
            >
              Month
            </label>
            <MonthInput
              id="yearMonth"
              value={yearMonth}
              onValueChange={handleMonthChange}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>

          {isLoading && <span className="text-gray-500 text-sm">Loading…</span>}
        </div>
      </Card>

      {/* Data source notice */}
      {dataSource === 'snapshot' && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-amber-700 text-sm">
          Showing snapshot data
          {snapshottedAt
            ? ` taken on ${new Date(snapshottedAt).toLocaleString()}`
            : ''}
          . Room fees or readings updated after this date will not be reflected
          here — use Monthly Records for the live total.
        </div>
      )}
      {dataSource === 'live' && (
        <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 px-4 py-2 text-blue-700 text-sm">
          No snapshot found for this month. Showing live data from utility
          records.
        </div>
      )}

      {/* Validation error */}
      {error && (
        <p className="mb-4 rounded-md bg-red-50 px-4 py-2 text-red-600 text-sm">
          {error}
        </p>
      )}

      {/* Export actions */}
      {bills.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <span className="text-gray-600 text-sm">
            {bills.length} bill{bills.length > 1 ? 's' : ''} ready
          </span>
          <Button
            onClick={exportReportPdf}
            disabled={isExporting}
            variant="secondary"
          >
            {isExporting ? 'Exporting…' : 'Export Report PDF'}
          </Button>
        </div>
      )}

      {/* Report table */}
      {bills.length > 0 && (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <ReportByRowTemplate bills={bills} yearMonth={yearMonth} />
          </div>
        </Card>
      )}
    </div>
  )
}
